// Grouped series list (by station+date) with search-as-in-Feed, an INLINE
// accordion drill (details open directly under the card, not in a page
// footer), and a floating «Свернуть / Наверх». The drill renders the per-config
// |dR| curves (in-chart config filter chips overlay several configs on one
// graph), the honest «○ без эталона» empty graph, and the slices table.
//
// Data:
//   list   ← ConvergenceSeriesListResponse.series  (ConvergenceSeriesListItem)
//   curves ← useConvergenceCurves(tag, configs) — fetched lazily on expand,
//            one request per config, cached for the session (re-open is free).

import { useEffect, useRef, useState } from 'react';
import type { RefObject } from 'react';

import type {
  ConvergenceCurvePoint,
  ConvergenceCurveResponse,
  ConvergenceSeriesListItem,
} from '../../api/types';
import { useConvergenceCurves } from '../../hooks/useAnalytics';
import { formatConfig, formatLen, formatObsDate, formatSec } from './convergenceScales';

const CONFIG_COLOR_VAR: Record<string, string> = {
  all4: 'var(--ink)',
  gps: 'var(--gps)',
  gal: 'var(--gal)',
  glo: 'var(--glo)',
  bds: 'var(--bds)',
};
const configColor = (k: string): string => CONFIG_COLOR_VAR[k] ?? 'var(--ink)';

function seriesStatus(s: ConvergenceSeriesListItem): 'result' | 'partial' | 'pending' {
  if (s.failed_count > 0 && s.done_count < s.slice_count) return 'partial';
  if (s.done_count < s.slice_count) return 'pending';
  return 'result';
}
function matchSeries(s: ConvergenceSeriesListItem, q: string): boolean {
  const t = q.trim().toLowerCase();
  if (!t) return true;
  return [s.station_code, s.tag, ...(s.gnss_configs ?? [])].some((f) =>
    f.toLowerCase().includes(t),
  );
}
function groupByDate(
  list: ConvergenceSeriesListItem[],
): [string, ConvergenceSeriesListItem[]][] {
  const map = new Map<string, ConvergenceSeriesListItem[]>();
  for (const s of list) {
    if (!map.has(s.obs_date)) map.set(s.obs_date, []);
    map.get(s.obs_date)!.push(s);
  }
  return [...map.entries()].sort((a, b) => (a[0] < b[0] ? 1 : -1));
}

export function ConvergenceSeriesList({
  list,
  scrollRef,
  initialLimit = 6,
  focusTag = null,
  focusStation = null,
  focusNonce = 0,
}: {
  list: ConvergenceSeriesListItem[];
  /** the scroll container (.analytics-screen body) so float-buttons + sticky work */
  scrollRef: RefObject<HTMLElement>;
  initialLimit?: number;
  /** matrix → list drill: open this series' accordion + filter to its station.
   *  `focusNonce` bumps on every matrix station tap to retrigger. */
  focusTag?: string | null;
  focusStation?: string | null;
  focusNonce?: number;
}) {
  const [searchOpen, setSearchOpen] = useState(false);
  const [q, setQ] = useState('');
  const [openTag, setOpenTag] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const cardRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const filtered = list.filter((s) => matchSeries(s, q));
  const shown = expanded || q ? filtered : filtered.slice(0, initialLimit);
  const groups = groupByDate(shown);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onScroll = () => setScrolled(el.scrollTop > 8);
    el.addEventListener('scroll', onScroll);
    return () => el.removeEventListener('scroll', onScroll);
  }, [scrollRef]);

  // Matrix → list drill: a station tap (focusNonce bump) opens that station's
  // latest series accordion, filters the list to the station, and scrolls to
  // the card. `scrollIntoView` works regardless of which ancestor scrolls.
  useEffect(() => {
    if (!focusNonce) return;
    setSearchOpen(true);
    setQ(focusStation ?? '');
    setExpanded(true);
    if (!focusTag) return;
    setOpenTag(focusTag);
    const id = window.setTimeout(() => {
      cardRefs.current[focusTag]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 60);
    return () => window.clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusNonce]);

  const collapse = () => {
    if (openTag) {
      const el = cardRefs.current[openTag];
      setOpenTag(null);
      if (el && scrollRef.current)
        scrollRef.current.scrollTo({ top: Math.max(0, el.offsetTop - 70), behavior: 'smooth' });
      return;
    }
    if (expanded) {
      setExpanded(false);
      scrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };
  const floatLabel = openTag ? 'Свернуть' : expanded ? 'Свернуть список' : null;
  const floatVisible = openTag != null || expanded || scrolled;

  return (
    <>
      <div className={`list-sticky${scrolled ? ' scrolled' : ''}`}>
        <div className="at-sec-head">
          <h3 className="at-sec-title">серии · по станциям</h3>
          <span className="at-sec-aside">{list.length} серий</span>
        </div>
        <div className="feed-search-row" onClick={() => !searchOpen && setSearchOpen(true)}>
          <button
            type="button"
            className={`feed-search-icon-btn${searchOpen ? ' active' : ''}`}
            onClick={(e) => {
              e.stopPropagation();
              setSearchOpen((v) => !v);
            }}
            aria-label="поиск"
          >
            <svg viewBox="0 0 24 24">
              <circle cx="11" cy="11" r="7" />
              <path d="m20 20-3.5-3.5" />
            </svg>
          </button>
          {searchOpen ? (
            <input
              className="feed-search-input"
              autoFocus
              placeholder="код станции или тег…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              data-testid="conv-series-search"
            />
          ) : (
            <span className="feed-search-hint">поиск по станции или тегу серии</span>
          )}
          {searchOpen && q && (
            <button
              type="button"
              className="feed-search-clear"
              onClick={(e) => {
                e.stopPropagation();
                setQ('');
              }}
            >
              ×
            </button>
          )}
        </div>
        {searchOpen && q && (
          <div className="feed-search-meta">
            найдено: {filtered.length} из {list.length}
          </div>
        )}
      </div>

      <div className="st-cards">
        {groups.map(([date, items]) => (
          <div className="conv-date-group" key={date}>
            <div className="st-group-head">{formatObsDate(date)}</div>
            {items.map((s) => (
              <div key={s.tag} ref={(el) => (cardRefs.current[s.tag] = el)}>
                <ConvergenceSeriesCard
                  series={s}
                  open={openTag === s.tag}
                  onToggle={() => setOpenTag((cur) => (cur === s.tag ? null : s.tag))}
                />
              </div>
            ))}
          </div>
        ))}
        {filtered.length === 0 && (
          <div className="at-empty">ничего не найдено по запросу «{q}»</div>
        )}
      </div>

      {!q && filtered.length > initialLimit && (
        <button type="button" className="list-more" onClick={() => setExpanded((v) => !v)}>
          {expanded ? (
            '↑ свернуть список'
          ) : (
            <>
              показать все {list.length} серий <span className="cnt">· сейчас {initialLimit}</span> →
            </>
          )}
        </button>
      )}

      <div className={`float-actions${floatVisible ? '' : ' hidden'}`}>
        {floatLabel && (
          <button type="button" className="float-btn" onClick={collapse}>
            <svg viewBox="0 0 24 24"><path d="m6 15 6-6 6 6" /></svg>
            {floatLabel}
          </button>
        )}
        <button
          type="button"
          className="float-btn ghost"
          title="Наверх"
          onClick={() => scrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' })}
        >
          <svg viewBox="0 0 24 24"><path d="M12 19V5M5 12l7-7 7 7" /></svg>
        </button>
      </div>
    </>
  );
}

function ConvergenceSeriesCard({
  series,
  open,
  onToggle,
}: {
  series: ConvergenceSeriesListItem;
  open: boolean;
  onToggle: () => void;
}) {
  const [showTech, setShowTech] = useState(false);
  const status = seriesStatus(series);
  const pct = series.slice_count > 0 ? (series.done_count / series.slice_count) * 100 : 0;
  const fillCls = status === 'partial' ? 'partial' : status === 'pending' ? 'pending' : '';

  return (
    <div
      className={`st-card${open ? ' open active-curve' : ''}`}
      data-screen-label={`Серия ${series.station_code}`}
      data-testid={`conv-card-${series.tag}`}
    >
      <div className="st-head" onClick={onToggle}>
        <div className="st-line1">
          <span className="st-code">{series.station_code}</span>
          <span className="st-cfgchips">
            {series.gnss_configs.map((c) => (
              <span key={c} className={`st-cfgchip ${c}`}>
                {formatConfig(c)}
              </span>
            ))}
          </span>
        </div>
        <div className="st-date">{formatObsDate(series.obs_date)}</div>

        {series.lengths_h && series.lengths_h.length > 0 && (
          <div className="st-intervals">
            интервалы:{' '}
            {series.lengths_h
              .map((l) => (l % 1 === 0 ? `${l}` : l.toFixed(1)))
              .join(' ')}{' '}
            ч
          </div>
        )}

        <div className="st-foot">
          <div className="st-prog">
            <div className="st-prog-track">
              <span className={`st-prog-fill ${fillCls}`} style={{ width: `${pct}%` }} />
            </div>
            <span className="st-prog-txt">
              {series.done_count}/{series.slice_count}
              {series.failed_count > 0 && <span className="bad"> · {series.failed_count} ✕</span>}
            </span>
          </div>
          {status === 'result' && <span className="st-badge result">● результат</span>}
          {status === 'partial' && <span className="st-badge partial">◑ частично</span>}
          {status === 'pending' && <span className="st-badge pending">◔ в работе</span>}
          <span className="st-chevron">
            <svg viewBox="0 0 24 24"><path d="m6 9 6 6 6-6" /></svg>
          </span>
        </div>

        <div
          className="st-techid"
          onClick={(e) => {
            e.stopPropagation();
            setShowTech((v) => !v);
          }}
        >
          <button type="button">
            {showTech ? <span className="val">{series.tag}</span> : 'тех. идентификатор серии'}
          </button>
        </div>
      </div>
      {open && <ConvergenceSeriesDrill series={series} />}
    </div>
  );
}

const DRILL_BOX = { w: 330, h: 188, padL: 30, padT: 12, padR: 12, padB: 26 };

function ConvergenceSeriesDrill({ series }: { series: ConvergenceSeriesListItem }) {
  const cfgs = series.gnss_configs;
  const [active, setActive] = useState<Set<string>>(
    () => new Set(cfgs[0] ? [cfgs[0]] : []),
  );
  const toggle = (c: string) =>
    setActive((prev) => {
      const n = new Set(prev);
      if (n.has(c)) {
        if (n.size > 1) n.delete(c);
      } else n.add(c);
      return n;
    });

  // One request per config, cached for the session — re-opening is free.
  const curvesQ = useConvergenceCurves(series.tag, cfgs);

  if (curvesQ.isLoading) {
    return <div className="st-drawer"><div className="at-empty">загрузка кривой…</div></div>;
  }
  if (curvesQ.isError || !curvesQ.data) {
    return (
      <div className="st-drawer">
        <div className="at-empty">
          ошибка загрузки кривой: {(curvesQ.error as Error)?.message ?? 'неизвестная'}
        </div>
      </div>
    );
  }

  const curves: Record<string, ConvergenceCurveResponse> = curvesQ.data;
  const activeCfgs = cfgs.filter((c) => active.has(c));

  // Shared X grid = union of every config's point lengths, ascending.
  const lengths = [
    ...new Set(
      Object.values(curves).flatMap((r) => r.points.map((p) => p.length_nominal_h)),
    ),
  ].sort((a, b) => a - b);

  const pointAt = (cfg: string, l: number): ConvergenceCurvePoint | undefined =>
    curves[cfg]?.points.find((p) => p.length_nominal_h === l);

  // Any reference point among the ACTIVE configs?
  const hasReference = activeCfgs.some((c) =>
    (curves[c]?.points ?? []).some((p) => p.dr_3d_mm != null),
  );

  const primary = activeCfgs[0] ?? cfgs[0]!;

  // Y scale from the visible |dR| / σ values across active configs.
  const vis: number[] = [];
  for (const c of activeCfgs) {
    for (const p of curves[c]?.points ?? []) {
      if (p.dr_3d_mm != null) vis.push(p.dr_3d_mm);
      if (hasReference && p.sigma_3d_mm != null) vis.push(p.sigma_3d_mm);
    }
  }
  const yTop = Math.max(10, ...vis, hasReference ? 0 : 60) * 1.1;
  const x0 = DRILL_BOX.padL;
  const w = DRILL_BOX.w - DRILL_BOX.padL - DRILL_BOX.padR;
  const h = DRILL_BOX.h - DRILL_BOX.padT - DRILL_BOX.padB;
  const n = Math.max(1, lengths.length - 1);
  const idxOf = new Map(lengths.map((l, i) => [l, i]));
  const sx = (l: number) => x0 + ((idxOf.get(l) ?? 0) / n) * w;
  const sy = (v: number) => DRILL_BOX.padT + h - (Math.max(0, Math.min(v, yTop)) / yTop) * h;

  return (
    <div className="st-drawer">
      <div className="drawer-head">
        <span className="drawer-title">|dR| по длинам интервалов</span>
        <span className="drawer-sub">
          {series.station_code} · {formatObsDate(series.obs_date)}
        </span>
      </div>

      <div className="chart-chiprow">
        <span className="lab">конфиги:</span>
        {cfgs.map((c) => (
          <span
            key={c}
            className={`at-chip cfg-${c}${active.has(c) ? ' on' : ''}`}
            onClick={() => toggle(c)}
            data-testid={`conv-drill-cfg-${c}`}
          >
            <span className="dot" />
            {formatConfig(c)}
          </span>
        ))}
      </div>

      {!hasReference ? (
        <EmptyConvergenceGraph series={series} lengths={lengths} />
      ) : (
        <div className="at-chart-card">
          <svg
            className="at-chart-svg"
            viewBox={`0 0 ${DRILL_BOX.w} ${DRILL_BOX.h}`}
            role="img"
            aria-label="кривая сходимости станции"
          >
            {[0, 0.25, 0.5, 0.75, 1].map((f) => {
              const yv = f * yTop;
              return (
                <g key={f}>
                  <line x1={x0} y1={sy(yv)} x2={x0 + w} y2={sy(yv)} stroke="var(--line-soft)" strokeWidth={1} />
                  <text x={x0 - 5} y={sy(yv) + 3.5} textAnchor="end" fontSize={9} fill="var(--ink-4)" fontFamily="'JetBrains Mono', monospace">
                    {yv.toFixed(0)}
                  </text>
                </g>
              );
            })}
            {lengths.map((l) => (
              <text key={l} x={sx(l)} y={DRILL_BOX.h - 8} textAnchor="middle" fontSize={9} fill="var(--ink-4)" fontFamily="'JetBrains Mono', monospace">
                {formatLen(l)}
              </text>
            ))}
            {/* one polyline + markers per active config (all4 painted last) */}
            {[...activeCfgs].reverse().map((cfg) => {
              const color = configColor(cfg);
              const pts = curves[cfg]?.points ?? [];
              const line = pts
                .filter((p) => p.dr_3d_mm != null)
                .sort((a, b) => a.length_nominal_h - b.length_nominal_h)
                .map((p) => `${sx(p.length_nominal_h)},${sy(p.dr_3d_mm!)}`)
                .join(' ');
              return (
                <g key={cfg}>
                  {line && (
                    <polyline
                      points={line}
                      fill="none"
                      stroke={color}
                      strokeWidth={cfg === 'all4' ? 2.6 : 2.2}
                      strokeLinejoin="round"
                    />
                  )}
                  {pts.map((p, i) =>
                    p.dr_3d_mm != null ? (
                      <circle key={i} cx={sx(p.length_nominal_h)} cy={sy(p.dr_3d_mm)} r={3} fill={color} />
                    ) : p.no_solution ? (
                      <circle key={i} cx={sx(p.length_nominal_h)} cy={DRILL_BOX.padT + h - 4} r={3.4} fill="none" stroke="var(--bad)" strokeDasharray="2 2" />
                    ) : null,
                  )}
                </g>
              );
            })}
          </svg>
          <div className="at-legend">
            {activeCfgs.map((c) => (
              <span className="at-leg" key={c}>
                <span className="ln" style={{ borderColor: configColor(c) }} />
                {formatConfig(c)}
              </span>
            ))}
          </div>
        </div>
      )}

      <table className="at-pt">
        <thead>
          <tr>
            <th>длина</th>
            <th>|dR|, мм</th>
            <th>σ3D, мм</th>
            <th>время</th>
            <th style={{ textAlign: 'left' }}>{`статус · ${formatConfig(primary)}`}</th>
          </tr>
        </thead>
        <tbody>
          {lengths
            .slice()
            .sort((a, b) => b - a)
            .map((l) => {
              const p = pointAt(primary, l);
              const noref = !hasReference;
              return (
                <tr key={l}>
                  <td>{formatLen(l)}</td>
                  <td>{noref ? '○' : p?.dr_3d_mm != null ? p.dr_3d_mm.toFixed(1) : '—'}</td>
                  <td>{p?.sigma_3d_mm != null ? p.sigma_3d_mm.toFixed(1) : '—'}</td>
                  <td>{formatSec(p?.processing_seconds ?? null)}</td>
                  <td style={{ textAlign: 'left', color: p?.no_solution ? 'var(--bad)' : 'var(--ink-2)' }}>
                    {p?.no_solution ? p.error_summary || 'нет решения' : noref ? '○ без эталона' : p?.state ?? 'done'}
                  </td>
                </tr>
              );
            })}
        </tbody>
      </table>
    </div>
  );
}

/** Honest empty graph — series computed but no IGS reference, so |dR| can't be
 *  plotted. σ3D still lives in the table. */
function EmptyConvergenceGraph({
  series,
  lengths,
}: {
  series: ConvergenceSeriesListItem;
  lengths: number[];
}) {
  const x0 = DRILL_BOX.padL;
  const w = DRILL_BOX.w - DRILL_BOX.padL - DRILL_BOX.padR;
  const sx = (l: number) =>
    x0 + (lengths.indexOf(l) / Math.max(1, lengths.length - 1)) * w;
  return (
    <div className="empty-graph" data-testid="conv-drill-noref">
      <svg className="at-chart-svg eg-ghost" viewBox={`0 0 ${DRILL_BOX.w} ${DRILL_BOX.h}`} aria-hidden="true">
        {[0, 0.25, 0.5, 0.75, 1].map((f) => (
          <line
            key={f}
            x1={x0}
            y1={DRILL_BOX.padT + f * (DRILL_BOX.h - DRILL_BOX.padT - DRILL_BOX.padB)}
            x2={x0 + w}
            y2={DRILL_BOX.padT + f * (DRILL_BOX.h - DRILL_BOX.padT - DRILL_BOX.padB)}
            stroke="var(--line-soft)"
            strokeWidth={1}
          />
        ))}
        {lengths.map((l) => (
          <text key={l} x={sx(l)} y={DRILL_BOX.h - 8} textAnchor="middle" fontSize={9} fill="var(--ink-4)" fontFamily="'JetBrains Mono', monospace">
            {formatLen(l)}
          </text>
        ))}
      </svg>
      <div className="eg-overlay">
        <span className="eg-mark" />
        <span className="eg-title">○ без эталона</span>
        <span className="eg-body">
          серия {series.done_count}/{series.slice_count} посчитана, но опорных координат IGS на эту
          дату нет — точек качества |dR| построить не из чего. σ3D в таблице есть.
        </span>
      </div>
    </div>
  );
}
